/**
 * The message a distributor sends their shopper about an abandoned, charged
 * order. Option B, Jake 2026-09-28; the wording is his, approved verbatim.
 *
 * WE DO NOT SEND THIS. It is shown on the stranded-orders surface for the
 * distributor to copy and send themselves -- the shopper is theirs, and the
 * explanation has to come from a person (Jake, 2026-09-15: an unexplained
 * cancel/refund is "a trust-buster").
 *
 * EVERY FACT IS OPTIONAL, AND A MISSING ONE DROPS ITS PHRASE. The distributor
 * pastes this straight into an email, so a literal "{first name}", "undefined"
 * or "$0.00" would reach a shopper whose money we are holding. Each clause is
 * built only from the facts it has; the sentence around it still reads.
 */

const trimmed = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * "September 15, 2026" from an ISO timestamp, or "" when it will not parse.
 *
 * Only the DATE part is read, and it is formatted in UTC: the order was placed
 * on that calendar day, and reading the time through the distributor's browser
 * timezone would move a late-evening order onto the next day.
 */
function orderDate(placedAt) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed(placedAt));
  if (!match) return "";
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function dollars(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n) || n <= 0) return "";
  return (
    "$" +
    (n / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * Build the message. All arguments optional:
 *   firstName, sender, distributorName, storeName, orderLabel,
 *   placedAt (ISO string), amountCents (integer cents actually charged)
 */
export function buildAbandonedOrderMessage({
  firstName,
  sender,
  distributorName,
  storeName,
  orderLabel,
  placedAt,
  amountCents,
} = {}) {
  const name = trimmed(firstName) || "there";
  const who = trimmed(sender);
  const from = trimmed(distributorName);
  const store = trimmed(storeName);
  const order = trimmed(orderLabel);
  const date = orderDate(placedAt);
  const amount = dollars(amountCents);

  let intro = "";
  if (who && from) intro = `this is ${who} from ${from}. `;
  else if (who || from) intro = `this is ${who || from}. `;

  const orderPhrase =
    "your " +
    (store ? `${store} ` : "") +
    "order" +
    (order ? ` #${order}` : "") +
    (date ? ` from ${date}` : "");

  const refund = amount
    ? `I'm issuing a full refund of ${amount} to your card today.`
    : "I'm issuing a full refund to your card today.";

  return (
    `Hi ${name}, ${intro}I'm reaching out about ${orderPhrase}. ` +
    "We hit a problem fulfilling it on our end, so we won't be able to ship " +
    "it, and I'm very sorry about that. " +
    `${refund} ` +
    "If you'd like, I'm happy to help you re-order or find an alternative. " +
    "Just reply here."
  );
}
